// @ts-strict-ignore
import { ConverterHelper } from '../convert/converter.helper';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ConnectionVO, MetaElementUsageIdentityVO, MetaElementVO, TextureAreaUsageVO, TextValueVO } from '../vo/meta-element.vo';
import { ArticleInfoVO, ProjectOptionDefVO, ProjectOptionVO } from '@project/model/vo/project.vo';
import { JsonConvert } from '../convert/convert-core';
import { ArrayUtils } from './array-utils';
import { SharedUserRightVO } from '@user/model/vo/shared-user-right.vo';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { Dictionary } from 'typescript-collections';
import { UserVO } from '@user/model/vo/user.vo';

export class CloneUtils {

  private static getObject(object: any): any {
    // add replace function because JSON.stringify() skips undefined properties
    const replacer = (key, value) => value === undefined ? null : value;
    const str: string = JSON.stringify(object, replacer);
    return object = JSON.parse(str);
  }

  private static cloneVO(vo: any, type: string): any {
    if (!vo) {
      return null;
    }
    const object: any = this.getObject(vo);
    return JsonConvert.deserialize(object, type);
  }

  public static cloneArticleVO(articleVO: ArticleVO): ArticleVO {
    if (!articleVO) {
      return null;
    }
    const object: any = this.getObject(articleVO);
    return ConverterHelper.convertToArticleVO(object);
  }

  public static cloneDynamicArticlesMap(map: Dictionary<string, ArticleVO>): Dictionary<string, ArticleVO> {
    if (!map) {
      return null;
    }

    const result: Dictionary<string, ArticleVO> = new Dictionary<string, ArticleVO>();
    for (const key of map.keys()) {
      result.setValue(key, CloneUtils.cloneArticleVO(map.getValue(key)));
    }
    return result;
  }

  public static cloneArticleInfoVO(articleInfoVO: ArticleInfoVO): ArticleInfoVO {
    if (!articleInfoVO) {
      return null;
    }
    const object: any = this.getObject(articleInfoVO);
    return JsonConvert.deserialize(object, 'ArticleInfoVO');
  }

  public static cloneArticleInfoVOs(articleInfoVOs: ArticleInfoVO[]): ArticleInfoVO[] {
    if (!articleInfoVOs) {
      return null;
    }
    const result: ArticleInfoVO[] = [];
    for (const infoVO of articleInfoVOs) {
      result.push(this.cloneArticleInfoVO(infoVO));
    }
    return result;
  }

  public static cloneArticleUsageVO(articleUsageVO: ArticleUsageVO): ArticleUsageVO {
    if (!articleUsageVO) {
      return null;
    }
    const object: any = this.getObject(articleUsageVO);
    return JsonConvert.deserialize(object, 'ArticleUsageVO');
  }

  public static cloneTextureAreaUsageVO(textureUsage: TextureAreaUsageVO): TextureAreaUsageVO {
    if (!textureUsage) {
      return null;
    }
    const object: any = this.getObject(textureUsage);
    return JsonConvert.deserialize(object, 'TextureAreaUsageVO');
  }

  public static cloneMetaElementVO(metaElementVO: MetaElementVO): MetaElementVO {
    if (!metaElementVO) {
      return null;
    }
    const object: any = this.getObject(metaElementVO);
    return ConverterHelper.convertToMetaElementVO(object);
  }

  public static cloneConnectionVO(connectionVO: ConnectionVO): ConnectionVO {
    if (!connectionVO) {
      return null;
    }
    const object: any = this.getObject(connectionVO);
    return JsonConvert.deserialize(object, 'ConnectionVO');
  }

  public static cloneMetaElementUsageIdentityVO(vo: MetaElementUsageIdentityVO): MetaElementUsageIdentityVO {
    if (!vo) {
      return null;
    }
    const object: any = this.getObject(vo);
    return JsonConvert.deserialize(object, 'MetaElementUsageIdentityVO');
  }

  public static cloneTextValueVO(vo: TextValueVO): TextValueVO {
    if (!vo) {
      return null;
    }
    const object: any = this.getObject(vo);
    return ConverterHelper.convertToTextValueVO(object);
  }

  public static cloneSharedUserRightVOs(vos: SharedUserRightVO[]): SharedUserRightVO[] {
    if (ArrayUtils.isEmpty(vos)) {
      return [];
    }
    const object: any = this.getObject(vos);
    return JsonConvert.deserialize(object, '[SharedUserRightVO]');
  }

  public static cloneProjectGroupVO(vo: ProjectGroupVO): ProjectGroupVO {
    if (!vo) {
      return null;
    }
    const object: any = this.getObject(vo);
    return JsonConvert.deserialize(object, 'ProjectGroupVO');
  }

  public static cloneUserVO(vo: UserVO): UserVO {
    if (!vo) {
      return null;
    }
    const object: any = this.getObject(vo);
    return JsonConvert.deserialize(object, 'UserVO');
  }

  public static cloneProjectOptionVO(vo: ProjectOptionVO): ProjectOptionVO {
    return this.cloneVO(vo, 'ProjectOptionVO');
  }

  public static cloneProjectOptionDefVO(vo: ProjectOptionDefVO): ProjectOptionDefVO {
    return this.cloneVO(vo, 'ProjectOptionDefVO');
  }
}
